import * as React from 'react'
import { View as ViewNative } from './View'

export interface LinearGradientProps {
  colors: (string | number)[]
  start: { x: number; y: number }
  end: { x: number; y: number }
  locations: number[]
  style?: any
}

const LinearGradient: React.FC<Partial<LinearGradientProps>> = (
  props: React.PropsWithChildren<LinearGradientProps>
) => {
  const [state, setState] = React.useState({ width: 1, height: 1 })

  const measure = React.useCallback(({ nativeEvent }) => {
    setState({
      width: nativeEvent.layout.width,
      height: nativeEvent.layout.height
    })
  }, [])

  const getAngle = () => {
    const angle =
      Math.atan2(
        state.width * (props.end.y - props.start.y),
        state.height * (props.end.x - props.start.x)
      ) +
      Math.PI / 2
    return `${angle}rad`
  }

  const getColors = () =>
    props.colors
      .map((color, index) => {
        const location = props.locations[index]
        let locationStyle = ''
        if (location) {
          locationStyle = ` ${location * 100}%`
        }
        return color + locationStyle
      })
      .join(',')

  return (
    <ViewNative
      style={{
        height: '100%',
        backgroundImage: `linear-gradient(${getAngle()},${getColors()})`
      }}
      onLayout={measure}
    >
      {props.children}
    </ViewNative>
  )
}

LinearGradient.defaultProps = {
  start: {
    x: 0.5,
    y: 0
  },
  end: {
    x: 0.5,
    y: 1
  },
  locations: [],
  colors: []
}

function radiansToPoints(angle) {
  const segment = Math.floor((angle / Math.PI) * 2) + 2
  const diagonal = ((1 / 2) * segment + 1 / 4) * Math.PI
  const op = Math.cos(Math.abs(diagonal - angle)) * Math.sqrt(2)
  const x = op * Math.cos(angle)
  const y = op * Math.sin(angle)

  return {
    start: {
      x: x < 0 ? 1 : 0,
      y: y < 0 ? 1 : 0
    },
    end: {
      x: x >= 0 ? x : x + 1,
      y: y >= 0 ? y : y + 1
    }
  }
}

function degreesToPoints(angle) {
  return radiansToPoints(((angle + 90) / 180) * Math.PI)
}

export default Object.assign(LinearGradient, {
  degreesToPoints,
  radiansToPoints
})
